// Vinterliste crypto module — see SECURITY.md for the model.
//
// Pure, side-effect-free helpers around libsodium. Designed to be called from
// both the browser (via Vite/ESM) and Bun (for tests). Nothing in here touches
// the network, IndexedDB, or the SQLite file. All randomness comes from
// libsodium's CSPRNG.

// We need the SUMO build of libsodium because the standard `libsodium-wrappers`
// ships without `crypto_pwhash` (Argon2id), which the spec mandates. The
// runtime-conditional loader in `./sodium.ts` papers over the package's
// broken ESM entry (different mechanism per runtime — see that file).
import sodium from './sodium';

export type Bytes = Uint8Array;

let readyPromise: Promise<void> | null = null;

/** Must be awaited once before calling anything else. Cached after first call. */
export function ready(): Promise<void> {
  if (!readyPromise) readyPromise = sodium.ready;
  return readyPromise;
}

// --- Constants ---------------------------------------------------------------
// Argon2id profile is MODERATE: ~256 MiB memory, ~3 iterations on libsodium's
// reference machine. Keep these stable: if you ever raise them, store the
// per-user parameters next to the salts so old accounts still unlock.
export const KDF_OPSLIMIT = (): number => sodium.crypto_pwhash_OPSLIMIT_MODERATE;
export const KDF_MEMLIMIT = (): number => sodium.crypto_pwhash_MEMLIMIT_MODERATE;
export const KDF_ALG = (): number => sodium.crypto_pwhash_ALG_ARGON2ID13;

export const SALT_BYTES = 16;            // crypto_pwhash_SALTBYTES
export const KEK_BYTES = 32;             // crypto_aead_xchacha20poly1305_ietf_KEYBYTES
export const NONCE_BYTES = 24;           // crypto_aead_xchacha20poly1305_ietf_NPUBBYTES
export const DEK_BYTES = 32;
export const VERIFIER_BYTES = 32;        // arbitrary; fits crypto_pwhash output

// Recovery code: 24 chars of Crockford-ish base32 → 120 bits of entropy.
// Layout: XXXX-XXXX-XXXX-XXXX-XXXX-XXXX (groups of 4 for readability).
const RECOVERY_ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ23456789'; // 30 chars
const RECOVERY_GROUPS = 6;
const RECOVERY_GROUP_LEN = 4;
const RECOVERY_TOTAL_CHARS = RECOVERY_GROUPS * RECOVERY_GROUP_LEN; // 24

// --- Random helpers ----------------------------------------------------------
export function randomBytes(n: number): Bytes {
  return sodium.randombytes_buf(n);
}

export function generateDek(): Bytes {
  return randomBytes(DEK_BYTES);
}

export function generateSalt(): Bytes {
  return randomBytes(SALT_BYTES);
}

export function generateNonce(): Bytes {
  return randomBytes(NONCE_BYTES);
}

/**
 * Generate a high-entropy human-typeable recovery code.
 *
 * Why a custom alphabet: the recovery code is read aloud and typed back in,
 * so we drop visually-ambiguous characters (0/O, 1/I/L, U). 30 chars × 24
 * positions ≈ 117.8 bits of entropy, comfortably above the 120-bit target
 * once you account for grouping dashes being non-secret.
 */
export function generateRecoveryCode(): string {
  const out: string[] = [];
  // Rejection-sample uniformly from a 30-char alphabet using bytes.
  // 256 mod 30 == 16, so accept bytes < 240 (8 * 30) to keep distribution uniform.
  const acceptMax = 240;
  const buf = randomBytes(RECOVERY_TOTAL_CHARS * 2); // oversample
  let bi = 0;
  while (out.length < RECOVERY_TOTAL_CHARS) {
    if (bi >= buf.length) {
      // Extremely unlikely, but top up if we burned through the buffer.
      const extra = randomBytes(RECOVERY_TOTAL_CHARS);
      buf.set(extra, 0);
      bi = 0;
    }
    const b = buf[bi++]!;
    if (b < acceptMax) out.push(RECOVERY_ALPHABET[b % RECOVERY_ALPHABET.length]!);
  }
  const groups: string[] = [];
  for (let i = 0; i < RECOVERY_GROUPS; i++) {
    groups.push(out.slice(i * RECOVERY_GROUP_LEN, (i + 1) * RECOVERY_GROUP_LEN).join(''));
  }
  return groups.join('-');
}

/** Normalise a recovery code before hashing: strip whitespace/dashes, uppercase. */
export function normalizeRecoveryCode(code: string): string {
  return code.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

// --- KDF ---------------------------------------------------------------------
/**
 * Derive a 32-byte key from a password (or recovery code) and salt.
 * Used for KEK derivation and for the auth verifier — caller supplies the
 * appropriate salt so the same primitive serves both purposes (see SECURITY.md).
 */
export function deriveKey(secret: string, salt: Bytes, outLen: number = KEK_BYTES): Bytes {
  if (salt.length !== SALT_BYTES) {
    throw new Error(`deriveKey: salt must be ${SALT_BYTES} bytes, got ${salt.length}`);
  }
  return sodium.crypto_pwhash(
    outLen,
    secret,
    salt,
    KDF_OPSLIMIT(),
    KDF_MEMLIMIT(),
    KDF_ALG(),
  );
}

// --- AEAD --------------------------------------------------------------------
export interface Sealed {
  ciphertext: Bytes;
  nonce: Bytes;
}

/**
 * Encrypt `plaintext` under `key` with a fresh nonce. The nonce is returned
 * alongside the ciphertext; it is not secret and is meant to be stored next
 * to it.
 */
export function aeadEncrypt(plaintext: Bytes, key: Bytes): Sealed {
  if (key.length !== KEK_BYTES) {
    throw new Error(`aeadEncrypt: key must be ${KEK_BYTES} bytes, got ${key.length}`);
  }
  const nonce = generateNonce();
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintext,
    null,           // no associated data
    null,           // no secret nonce
    nonce,
    key,
  );
  return { ciphertext, nonce };
}

export function aeadDecrypt(sealed: Sealed, key: Bytes): Bytes {
  if (key.length !== KEK_BYTES) {
    throw new Error(`aeadDecrypt: key must be ${KEK_BYTES} bytes, got ${key.length}`);
  }
  if (sealed.nonce.length !== NONCE_BYTES) {
    throw new Error(`aeadDecrypt: nonce must be ${NONCE_BYTES} bytes`);
  }
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null,           // no secret nonce
    sealed.ciphertext,
    null,           // no associated data
    sealed.nonce,
    key,
  );
}

// --- DEK wrap / unwrap (thin convenience over AEAD) --------------------------
export function wrapDek(dek: Bytes, kek: Bytes): Sealed {
  if (dek.length !== DEK_BYTES) {
    throw new Error(`wrapDek: dek must be ${DEK_BYTES} bytes`);
  }
  return aeadEncrypt(dek, kek);
}

export function unwrapDek(sealed: Sealed, kek: Bytes): Bytes {
  const dek = aeadDecrypt(sealed, kek);
  if (dek.length !== DEK_BYTES) {
    throw new Error(`unwrapDek: unwrapped DEK has wrong length ${dek.length}`);
  }
  return dek;
}

// --- Activity payload encryption --------------------------------------------
/**
 * Shape of a private activity's encrypted payload. Anything that identifies
 * a private activity to a human must go here, not into a server column.
 */
export interface PrivatePayload {
  title: string;
  tags: string[];
  /** Optional free-text body. Plain text (markdown rendering is a future polish). */
  description?: string;
  loc_label?: string;
  loc_lat?: number;
  loc_lng?: number;
  scheduled_at?: number;
}

const utf8 = new TextEncoder();
const utf8d = new TextDecoder('utf-8', { fatal: true });

export function encryptPayload(payload: PrivatePayload, dek: Bytes): Sealed {
  const json = JSON.stringify(payload);
  return aeadEncrypt(utf8.encode(json), dek);
}

export function decryptPayload(sealed: Sealed, dek: Bytes): PrivatePayload {
  const plaintext = aeadDecrypt(sealed, dek);
  const json = utf8d.decode(plaintext);
  return JSON.parse(json) as PrivatePayload;
}

// --- Auth verifier -----------------------------------------------------------
/**
 * Derive the auth verifier sent to the server. The verifier salt MUST be
 * distinct from the KEK salt — see SECURITY.md. We base64-encode the raw
 * bytes for transport because Bun.password.hash expects a string input.
 */
export function deriveAuthVerifier(password: string, authSalt: Bytes): string {
  const bytes = deriveKey(password, authSalt, VERIFIER_BYTES);
  return bytesToBase64(bytes);
}

// --- Encoding helpers --------------------------------------------------------
export function bytesToBase64(b: Bytes): string {
  return sodium.to_base64(b, sodium.base64_variants.ORIGINAL);
}

export function base64ToBytes(s: string): Bytes {
  return sodium.from_base64(s, sodium.base64_variants.ORIGINAL);
}

export function bytesEqual(a: Bytes, b: Bytes): boolean {
  if (a.length !== b.length) return false;
  return sodium.memcmp(a, b);
}

/** Best-effort zeroisation for secrets that are no longer needed in memory. */
export function zero(b: Bytes): void {
  sodium.memzero(b);
}
